/**
 * Shared localStorage shape for the Companies page when no saved view is
 * active. Filters and columns travel together because they're two halves
 * of the same "what the user is currently looking at" state — saving a
 * view captures both, applying a view restores both, modifying either
 * marks the view dirty.
 */
export interface CompanyViewState {
  filters: Record<string, unknown>;
  columns: string[];
}

function key(slug: string): string {
  return `prism.${slug}.companies.viewState`;
}

const LEGACY_COLUMNS_KEY_PREFIX = "prism.";
function legacyColumnsKey(slug: string): string {
  return `${LEGACY_COLUMNS_KEY_PREFIX}${slug}.companies.columns`;
}

export function readCompanyViewState(
  slug: string,
): CompanyViewState | null {
  try {
    const raw = localStorage.getItem(key(slug));
    if (!raw) return migrateLegacy(slug);
    const parsed = JSON.parse(raw) as Partial<CompanyViewState>;
    return {
      filters:
        parsed.filters && typeof parsed.filters === "object"
          ? (parsed.filters as Record<string, unknown>)
          : {},
      columns: Array.isArray(parsed.columns)
        ? parsed.columns.filter((c): c is string => typeof c === "string")
        : [],
    };
  } catch {
    return null;
  }
}

export function writeCompanyViewState(
  slug: string,
  state: CompanyViewState,
): void {
  try {
    localStorage.setItem(key(slug), JSON.stringify(state));
    // Drop the legacy columns-only blob so we don't run two stores in
    // parallel and risk drift.
    localStorage.removeItem(legacyColumnsKey(slug));
  } catch {
    // Quota / private mode — fail silently; in-memory state still works.
  }
}

export function clearCompanyViewState(slug: string): void {
  try {
    localStorage.removeItem(key(slug));
    localStorage.removeItem(legacyColumnsKey(slug));
  } catch {
    // Same — silent.
  }
}

/**
 * One-time migration from the columns-only key (set by an earlier build
 * of useColumnPreferences) to the unified viewState shape. Returns the
 * migrated value if any, else null.
 */
function migrateLegacy(slug: string): CompanyViewState | null {
  try {
    const raw = localStorage.getItem(legacyColumnsKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const columns = parsed.filter(
      (x): x is string => typeof x === "string",
    );
    const migrated: CompanyViewState = { filters: {}, columns };
    writeCompanyViewState(slug, migrated);
    return migrated;
  } catch {
    return null;
  }
}
